/**
 * ============================================================================
 * TREND TREE — GIFT WITH PURCHASE (GWP) HANDLER
 * ============================================================================
 * File:      assets/cart-gift-handler.js
 * Depends:   assets/gift-mapping.js (must load BEFORE this file)
 * Depends:   snippets/cart-gift-trigger-data.liquid (rendered in cart markup)
 * Depends:   snippets/cart-gift-variant-data.liquid  (rendered in cart markup)
 *
 * Purpose: Reads the live cart via the Shopify AJAX Cart API, runs it
 * through the rule engine in gift-mapping.js, and adds/removes gift line
 * items so the cart always reflects exactly the gifts it currently
 * qualifies for — no page refresh, no duplicate requests, no manual wiring
 * required beyond loading this script and rendering the two data snippets.
 *
 * PUBLIC API (available on window.TrendTreeGifts once this file loads):
 *   TrendTreeGifts.recalculate()   -> manually trigger a reconciliation pass
 *   TrendTreeGifts.getState()      -> returns the last known reconcile summary
 *
 * HOW RECALCULATION IS TRIGGERED AUTOMATICALLY:
 * This file wraps window.fetch to watch for calls to Shopify's cart
 * mutation endpoints (/cart/add, /cart/change, /cart/update, /cart/clear).
 * After any such call resolves, a debounced reconciliation runs. This means
 * NO theme integration is required — it works regardless of which button,
 * form, or app triggers the cart change. Our own internal add/remove calls
 * are tagged with a header so they don't recursively re-trigger themselves.
 * ============================================================================
 */

(function (window, document) {
  "use strict";

  if (!window.TrendTreeGifts || !window.TrendTreeGifts.config) {
    console.error(
      "[TrendTreeGifts] gift-mapping.js did not load before cart-gift-handler.js. Aborting."
    );
    return;
  }

  var CONFIG = window.TrendTreeGifts.config;
  var resolveGiftsForTags = window.TrendTreeGifts.resolveGiftsForTags;
  var getNextMilestone = window.TrendTreeGifts.getNextMilestone;

  var INTERNAL_HEADER = "X-TrendTree-Gift-Internal";
  var CART_MUTATION_PATTERNS = ["/cart/add", "/cart/change", "/cart/update", "/cart/clear"];
  var DEBOUNCE_MS = 200;

  var debounceTimer = null;
  var isReconciling = false;
  var pendingRerunRequested = false;
  var lastState = null;

  function log() {
    if (CONFIG.SETTINGS.debug) {
      console.log.apply(console, ["[TrendTreeGifts]"].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function warn() {
    console.warn.apply(console, ["[TrendTreeGifts]"].concat(Array.prototype.slice.call(arguments)));
  }

  /**
   * --------------------------------------------------------------------
   * DATA READERS — pull the two JSON blocks the Liquid snippets render.
   * Both are re-read fresh on every reconcile pass since the cart drawer/
   * page markup (and therefore these <script> tags) gets re-rendered by
   * the Section Rendering API after every mutation.
   * --------------------------------------------------------------------
   */
  function readJsonScript(id) {
    var el = document.getElementById(id);
    if (!el) {
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      warn("Failed to parse JSON from #" + id, err);
      return null;
    }
  }

  function getTriggerTagMap() {
    return readJsonScript("trendtree-gift-trigger-tags") || {};
  }

  function getGiftVariantMap() {
    return readJsonScript("trendtree-gift-variant-data") || {};
  }

  /**
   * --------------------------------------------------------------------
   * CART HELPERS
   * --------------------------------------------------------------------
   */
  function fetchCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (res) {
      if (!res.ok) {
        throw new Error("Failed to fetch /cart.js: " + res.status);
      }
      return res.json();
    });
  }

  function isGiftLine(item) {
    var props = item.properties || {};
    return props[CONFIG.LINE_ITEM_PROPS.giftFlagKey] === CONFIG.LINE_ITEM_PROPS.giftFlagValue;
  }

  /**
   * Builds the Set of gift-type tags present among the cart's NON-GIFT
   * line items. Gift lines are explicitly excluded so gifts never trigger
   * other gifts (spec requirement).
   */
  function buildPresentTagSet(cart, triggerTagMap) {
    var tagSet = new Set();
    cart.items.forEach(function (item) {
      if (isGiftLine(item)) {
        return;
      }
      var tags = triggerTagMap[String(item.product_id)];
      if (Array.isArray(tags)) {
        tags.forEach(function (tag) {
          tagSet.add(tag);
        });
      }
    });
    return tagSet;
  }

  /**
   * Cart subtotal EXCLUDING gift lines, in rupees (cart.js amounts are in
   * paise/cents, so we divide by 100). Used for both the threshold check
   * and progress-bar milestone math.
   *
   * MRP-BASED: uses `original_line_price` (the price BEFORE any discounts,
   * i.e. unit price x quantity) so that discount codes such as FESTIVE20,
   * automatic discounts and any other line-level or cart-level discounts
   * NEVER reduce the amount used for gift unlock. This matches the
   * "Total MRP" shown in the cart drawer and the progress bar.
   *
   * Falls back to `line_price` only if `original_line_price` is missing
   * (should not happen on current Shopify cart.js responses).
   */
  function getGiftExcludedSubtotal(cart) {
    var totalPaise = cart.items.reduce(function (sum, item) {
      if (isGiftLine(item)) {
        return sum;
      }
      var mrpLine =
        item.original_line_price !== undefined ? item.original_line_price : item.line_price;
      return sum + mrpLine;
    }, 0);
    return totalPaise / 100;
  }

  function getCurrentGiftLines(cart) {
    return cart.items
      .filter(isGiftLine)
      .map(function (item) {
        return {
          key: item.key,
          giftType: item.properties[CONFIG.LINE_ITEM_PROPS.giftTypeKey],
          quantity: item.quantity,
          variantId: item.variant_id
        };
      });
  }

  /**
   * --------------------------------------------------------------------
   * AJAX CART API — internal calls (flagged so our own fetch wrapper
   * doesn't recursively re-trigger reconciliation on them).
   * --------------------------------------------------------------------
   */
  function internalFetch(url, options) {
    options = options || {};
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      Accept: "application/json"
    });
    options.headers[INTERNAL_HEADER] = "1";
    return fetch(url, options);
  }

  function addGiftLine(giftKey, variantId, matchedRuleId) {
    var properties = {};
    properties[CONFIG.LINE_ITEM_PROPS.giftFlagKey] = CONFIG.LINE_ITEM_PROPS.giftFlagValue;
    properties[CONFIG.LINE_ITEM_PROPS.giftTypeKey] = giftKey;
    properties[CONFIG.LINE_ITEM_PROPS.giftRuleKey] = matchedRuleId || "unknown";

    return internalFetch("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        id: variantId,
        quantity: CONFIG.SETTINGS.maxGiftQtyPerType,
        properties: properties
      })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (errBody) {
          throw new Error("Failed to add gift " + giftKey + ": " + (errBody.description || res.status));
        });
      }
      log("Added gift:", giftKey);
      return res.json();
    });
  }

  function removeGiftLine(lineKey, giftKey) {
    return internalFetch("/cart/change.js", {
      method: "POST",
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (errBody) {
          throw new Error("Failed to remove gift " + giftKey + ": " + (errBody.description || res.status));
        });
      }
      log("Removed gift:", giftKey);
      return res.json();
    });
  }

  function correctGiftQuantity(lineKey, giftKey) {
    return internalFetch("/cart/change.js", {
      method: "POST",
      body: JSON.stringify({ id: lineKey, quantity: CONFIG.SETTINGS.maxGiftQtyPerType })
    }).then(function (res) {
      if (!res.ok) {
        throw new Error("Failed to correct quantity for gift " + giftKey);
      }
      log("Corrected quantity for gift:", giftKey);
      return res.json();
    });
  }

  /**
   * --------------------------------------------------------------------
   * CART DRAWER UI REFRESH
   * --------------------------------------------------------------------
   * Our internal /cart/add.js and /cart/change.js calls correctly update
   * the real Shopify cart on the server, but the theme's own cart drawer
   * only re-renders itself when ITS OWN buttons trigger a change (their
   * click handlers fetch + re-render together, not via a listenable
   * event). So after we mutate the cart ourselves, we independently
   * re-fetch the cart-drawer section's HTML via the Section Rendering
   * API and swap it into the live DOM, guaranteeing the visible drawer
   * matches the real cart regardless of theme internals.
   *
   * SECTION ID: assumes the cart drawer section's runtime id is
   * "cart-drawer" (the common convention when a section file is
   * referenced by that key in its section-group JSON). If your theme
   * uses a different id, this fetch will silently no-op (cart stays
   * correct server-side, just visually stale until next real interaction)
   * — check the Network tab for a request matching
   * "?section_id=..." while opening the cart normally to find the
   * real id, then update CART_DRAWER_SECTION_ID below.
   * --------------------------------------------------------------------
   */
  var CART_DRAWER_SECTION_ID = "cart-drawer";

  function refreshCartDrawerUI() {
    return fetch("/?section_id=" + CART_DRAWER_SECTION_ID, {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html) {
          return;
        }
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        var newDrawer = doc.querySelector("cart-drawer, #cart-drawer, [id='cart-drawer']");
        var liveDrawer = document.getElementById("cart-drawer");
        if (newDrawer && liveDrawer) {
          liveDrawer.innerHTML = newDrawer.innerHTML;
          log("Cart drawer UI refreshed.");
        } else {
          warn(
            "Could not find cart-drawer markup to swap in. Section id '" +
              CART_DRAWER_SECTION_ID +
              "' may be wrong for this theme."
          );
        }
      })
      .catch(function (err) {
        warn("Failed to refresh cart drawer UI:", err);
      });
  }

  /**
   * --------------------------------------------------------------------
   * CORE RECONCILIATION PASS
   * --------------------------------------------------------------------
   * 1. Read current cart + tag/variant data
   * 2. Determine which gifts SHOULD be in the cart right now
   * 3. Diff against what IS in the cart
   * 4. Remove what shouldn't be there, add what's missing, fix quantities
   * 5. Broadcast a summary event for the progress bar / gift manager UI
   */
  function reconcile() {
    if (isReconciling) {
      pendingRerunRequested = true;
      return Promise.resolve();
    }
    isReconciling = true;

    return fetchCart()
      .then(function (cart) {
        var triggerTagMap = getTriggerTagMap();
        var giftVariantMap = getGiftVariantMap();
        var presentTags = buildPresentTagSet(cart, triggerTagMap);
        var subtotal = getGiftExcludedSubtotal(cart);
        var meetsThreshold = subtotal >= CONFIG.GIFT_THRESHOLD;

        // Each rule applies its own minimum (minSubtotal, else GIFT_THRESHOLD)
        var resolved = resolveGiftsForTags(presentTags, subtotal);
        var desiredGiftKeys = resolved.giftKeys;

        var currentGiftLines = getCurrentGiftLines(cart);
        var currentGiftKeys = currentGiftLines.map(function (l) {
          return l.giftType;
        });

        var toRemove = currentGiftLines.filter(function (line) {
          return desiredGiftKeys.indexOf(line.giftType) === -1;
        });

        var toAdd = desiredGiftKeys.filter(function (key) {
          return currentGiftKeys.indexOf(key) === -1;
        });

        var toFixQuantity = currentGiftLines.filter(function (line) {
          return (
            desiredGiftKeys.indexOf(line.giftType) !== -1 &&
            line.quantity !== CONFIG.SETTINGS.maxGiftQtyPerType
          );
        });

        log("Reconcile:", {
          subtotal: subtotal,
          meetsThreshold: meetsThreshold,
          presentTags: Array.from(presentTags),
          desiredGiftKeys: desiredGiftKeys,
          toAdd: toAdd,
          toRemove: toRemove.map(function (l) {
            return l.giftType;
          })
        });

        // Removals first (frees up any edge cases before adding new lines)
        var removalChain = toRemove.reduce(function (chain, line) {
          return chain.then(function () {
            return removeGiftLine(line.key, line.giftType).catch(function (err) {
              warn(err.message);
            });
          });
        }, Promise.resolve());

        return removalChain
          .then(function () {
            var quantityFixChain = toFixQuantity.reduce(function (chain, line) {
              return chain.then(function () {
                return correctGiftQuantity(line.key, line.giftType).catch(function (err) {
                  warn(err.message);
                });
              });
            }, Promise.resolve());
            return quantityFixChain;
          })
          .then(function () {
            var additionChain = toAdd.reduce(function (chain, giftKey) {
              return chain.then(function () {
                var variantInfo = giftVariantMap[giftKey];
                if (!variantInfo || !variantInfo.variantId) {
                  warn(
                    "No variant data found for gift key '" +
                      giftKey +
                      "'. Check cart-gift-variant-data.liquid has this key wired up."
                  );
                  return;
                }
                if (!variantInfo.available) {
                  warn("Gift '" + giftKey + "' is out of stock — skipping add.");
                  return;
                }
                var matchedRuleId = CONFIG.RULES.find(function (rule) {
                  return rule.gifts.indexOf(giftKey) !== -1 && resolved.matchedRules.indexOf(rule.id) !== -1;
                });
                return addGiftLine(giftKey, variantInfo.variantId, matchedRuleId ? matchedRuleId.id : null).catch(
                  function (err) {
                    warn(err.message);
                  }
                );
              });
            }, Promise.resolve());
            return additionChain;
          })
          .then(function () {
            var madeChanges = toAdd.length > 0 || toRemove.length > 0 || toFixQuantity.length > 0;
            if (!madeChanges) {
              return null;
            }
            return refreshCartDrawerUI();
          })
          .then(function () {
            var nextMilestoneInfo = getNextMilestone(subtotal);
            lastState = {
              subtotal: subtotal,
              meetsThreshold: meetsThreshold,
              activeGiftKeys: desiredGiftKeys,
              matchedRules: resolved.matchedRules,
              nextMilestone: nextMilestoneInfo,
              updatedAt: Date.now()
            };
            document.dispatchEvent(
              new CustomEvent("trendtree:gifts-updated", { detail: lastState })
            );
            return lastState;
          });
      })
      .catch(function (err) {
        warn("Reconciliation failed:", err);
      })
      .then(function (result) {
        isReconciling = false;
        if (pendingRerunRequested) {
          pendingRerunRequested = false;
          return reconcile();
        }
        return result;
      });
  }

  function scheduleReconcile() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      reconcile();
    }, DEBOUNCE_MS);
  }

  /**
   * --------------------------------------------------------------------
   * FETCH INTERCEPTOR — auto-detects cart mutations from ANY source
   * (theme's own JS, apps, this file's own internal calls excluded).
   * --------------------------------------------------------------------
   */
  function installFetchInterceptor() {
    var originalFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isCartMutation = CART_MUTATION_PATTERNS.some(function (pattern) {
        return url.indexOf(pattern) !== -1;
      });
      var isInternal =
        init &&
        init.headers &&
        (init.headers[INTERNAL_HEADER] ||
          (typeof init.headers.get === "function" && init.headers.get(INTERNAL_HEADER)));

      var promise = originalFetch(input, init);

      if (isCartMutation && !isInternal) {
        promise
          .then(function () {
            scheduleReconcile();
          })
          .catch(function () {
            /* network error on the original call — nothing to reconcile */
          });
      }

      return promise;
    };
  }

  /**
   * --------------------------------------------------------------------
   * PUBLIC API + INIT
   * --------------------------------------------------------------------
   */
  window.TrendTreeGifts.recalculate = reconcile;
  window.TrendTreeGifts.getState = function () {
    return lastState;
  };

  /**
   * --------------------------------------------------------------------
   * GIFT LINK LOCKING
   * --------------------------------------------------------------------
   * Free gift lines in the cart must not be clickable, otherwise a
   * customer can open the gift product page from the cart. This:
   *   1. Strips the link (href) from any cart link pointing to a gift
   *      product, and makes it non-interactive. A MutationObserver
   *      re-applies this after every cart drawer re-render.
   *   2. Blocks clicks on gift links in the capture phase, as a safety
   *      net for the moment before step 1 runs.
   *   3. Redirects visitors who open a gift product page directly to
   *      the home page.
   * A gift handle is recognised either from cart-gift-variant-data.liquid
   * or because it contains "free-gift".
   * --------------------------------------------------------------------
   */
  function getGiftHandles() {
    var map = getGiftVariantMap();
    return Object.keys(map)
      .map(function (k) {
        return map[k] && map[k].handle;
      })
      .filter(Boolean);
  }

  function isGiftHandle(handle) {
    if (!handle) {
      return false;
    }
    handle = String(handle).toLowerCase();
    if (handle.indexOf("free-gift") !== -1) {
      return true;
    }
    return getGiftHandles().indexOf(handle) !== -1;
  }

  function handleFromHref(href) {
    var path = String(href || "").split("?")[0].split("#")[0];
    var parts = path.split("/products/");
    if (parts.length < 2) {
      return null;
    }
    return parts[1].split("/")[0];
  }

  function lockGiftLinks() {
    var anchors = document.querySelectorAll("a[href*='/products/']");
    Array.prototype.forEach.call(anchors, function (a) {
      if (!isGiftHandle(handleFromHref(a.getAttribute("href")))) {
        return;
      }
      a.removeAttribute("href");
      a.setAttribute("aria-disabled", "true");
      a.setAttribute("tabindex", "-1");
      a.style.pointerEvents = "none";
      a.style.cursor = "default";
    });
  }

  var lockTimer = null;
  function scheduleLockGiftLinks() {
    if (lockTimer) {
      return;
    }
    lockTimer = setTimeout(function () {
      lockTimer = null;
      lockGiftLinks();
    }, 100);
  }

  function installGiftLinkLock() {
    document.addEventListener(
      "click",
      function (e) {
        var a = e.target && e.target.closest ? e.target.closest("a[href*='/products/']") : null;
        if (a && isGiftHandle(handleFromHref(a.getAttribute("href")))) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    if (window.MutationObserver) {
      new MutationObserver(scheduleLockGiftLinks).observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
      });
    }
    lockGiftLinks();

    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (m && isGiftHandle(m[1])) {
      window.location.replace("/");
    }
  }

  installFetchInterceptor();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", installGiftLinkLock);
  } else {
    installGiftLinkLock();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reconcile);
  } else {
    reconcile();
  }
})(window, document);
